import React, { useEffect } from 'react';
import queryString from 'query-string';
import { connect } from 'react-redux';
import { Container } from 'reactstrap';
import { useIntl } from 'react-intl';
import { Redirect, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';
import { Location, LocationDescriptorObject } from 'history';
import { IError, INavItem, ICustomer, IUser } from '../../../helpers/types';
import * as actions from '../../../actions';
import NavBar from '../../layout/NavBar';
import PageMeta from '../../helpers/PageMeta';
import XpayPaypalAlert from '../../../components/XpayPaypalAlert';
import ConfirmSignUpForm from '../../../components/forms/ConfirmSignUpForm';
import ErrorPage from '../ErrorPage';
import { SOURCE, SOURCE_EMAIL } from '../../../helpers/constants';
import commonMessages from '../../../helpers/messages';
import { StyledLoading } from '../../../styled';
import Footer from '../../layout/Footer';
import { StyledLayout, StyledSignUpBlock, StyledSignUpHeadingBlock, StyledBGWrapper } from './styled';
import messages from './messages';

interface IConfirmSignUpProps {
  navItems?: INavItem[];
  error?: IError;
  validateSignUpTokenError?: IError;
  updateEmailWithTokensError?: IError;
  customerData?: ICustomer;
  user?: IUser;
  validateSignUpToken: (...args: any[]) => any;
  confirmSignUp: (...args: any[]) => any;
  updateEmailWithTokens: (...args: any[]) => any;
  getCustomerEmail: (...args: any[]) => any;
  loading: boolean;
}

const ConfirmSignUp = ({
  navItems = [],
  error,
  customerData,
  user,
  validateSignUpToken,
  validateSignUpTokenError,
  updateEmailWithTokensError,
  confirmSignUp,
  updateEmailWithTokens,
  getCustomerEmail,
  loading,
}: IConfirmSignUpProps): JSX.Element => {
  const theme = useTheme();
  const isMobile = !useMediaQuery({ minWidth: theme.gridBreakpoints.sm });
  const intl = useIntl();
  const location: Location<{ referrer?: LocationDescriptorObject }> = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');
  const token = urlParams.get('token');
  const securityToken = urlParams.get('securityToken');
  const emailToken = urlParams.get('emailToken');
  const referrer = urlParams.get('referrer');

  let customer = customerData;
  if (customer?.id !== id) {
    customer = undefined;
  }

  const { pathname, search } = location.state?.referrer || {};
  const referralSearch = new URLSearchParams(search);
  referralSearch.delete('signup_token');
  referralSearch.delete('id');
  const stateReferrer = {
    referrer: {
      pathname,
      search: referralSearch.toString(),
    },
  };

  const source = urlParams.get(SOURCE) || SOURCE_EMAIL;

  // Redirect to login after storing clean (No ID & Token) search params in state to be actioned after login
  const redirectToLogin = (mode: string) => {
    const emailParam = urlParams.get('email');
    let referrerObj = {};

    if (stateReferrer.referrer.pathname) {
      referrerObj = stateReferrer;
    } else if (emailParam) {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.set('email', emailParam);
      referrerObj = {
        referrer: {
          pathname: location.pathname,
          search: urlSearchParams.toString(),
        },
      };
    }

    return (
      <Redirect
        to={{
          pathname: `/${intl.locale}/login`,
          state: {
            message: intl.formatMessage(
              mode === SOURCE_EMAIL ? commonMessages.emailVerified : commonMessages.phoneVerified,
            ),
            source,
            ...referrerObj,
          },
        }}
      />
    );
  };

  // Redirect to destination with clean (No ID & Token) search params
  const redirectToDestination = (destination: string, mode: string) => (
    <Redirect
      to={{
        pathname: destination,
        search: referralSearch.toString(),
        state: {
          message: intl.formatMessage(
            mode === SOURCE_EMAIL ? commonMessages.emailVerified : commonMessages.phoneVerified,
          ),
        },
      }}
    />
  );

  // Update the email if securityToken and emailToken exists. For ex: update the email of partner's customer (eg: amazon customer)
  // who land on our login page thru a channel like amazon email
  useEffect(() => {
    if (securityToken && !loading && !customer?.email && !error && emailToken) {
      // here 422 => email already updated(but not signed up yet). 404 => error code where id present in url but incorrect(this is edge case). 403 => user trying to reuse signup link after successful signup(edge case)
      // @ts-ignore
      if (![422, 404, 403].includes(updateEmailWithTokensError?.status)) {
        const queryParams = { id, securityToken, emailToken };
        updateEmailWithTokens(queryParams);
      } else {
        getCustomerEmail(id);
      }
    }
  }, [
    updateEmailWithTokensError,
    securityToken,
    emailToken,
    updateEmailWithTokens,
    getCustomerEmail,
    id,
    loading,
    customer?.email,
    error,
  ]);

  // Customer landed, validate the id and token
  useEffect(() => {
    if (id && token && !customer?.token) {
      validateSignUpToken({ id, token, source });
    }
  }, [id, token, validateSignUpToken, source, customer]);

  // If user is coming back through a link that has a referrer query, store it in state through a redirect
  if (referrer) {
    return (
      <Redirect
        to={{
          pathname: `/${intl.locale}/signup/confirm`,
          search: queryString.stringify({ id, token, source }),
          state: { referrer: JSON.parse(referrer) },
        }}
      />
    );
  }

  // after successful sign up if an amazon user tries to sign up again using same amazon email with another email id, then redirect to login page
  if (securityToken && updateEmailWithTokensError && updateEmailWithTokensError.status === 403) {
    return redirectToLogin('email');
  }

  /*
    In general, below are 2 kinds of valid query params combinations for a sign up url:
      1. securityToken, emailToken, id (this is an amazon user)
      2. token, id, source (this is regular existing sign up url params)
    ((!securityToken || !emailToken || !id) && !token) => this is an amazon user sign up link. If securityToken, id and emailToken not present in url then show error page.
    ((!token || !id) && !securityToken) => this is a regular sign up link. If id & token not present in url then show error page
  */
  if (
    error ||
    (validateSignUpTokenError && validateSignUpTokenError.status !== 422) ||
    (updateEmailWithTokensError && updateEmailWithTokensError.status !== 422) ||
    ((!securityToken || !emailToken || !id) && !token) ||
    ((!token || !id) && !securityToken)
  ) {
    // @ts-ignore
    return <ErrorPage error={error || validateSignUpTokenError || updateEmailWithTokensError} />;
  }

  /*
    If error status is 422 and if amazon user then don't redirect to login page.
    This is an edge case but if user has already clicked amazon login link but not signed up (but email is updated meantime). 
    When user decides to sign up later(by clicking link), or by refreshing existing sign up age(with tokens), then we get 422, 
    so don't redirect amazon user with 422 to login page
  */
  if (
    (validateSignUpTokenError && validateSignUpTokenError.status === 422) ||
    (updateEmailWithTokensError && updateEmailWithTokensError.status !== 422) ||
    customer?.phoneVerified ||
    customer?.emailVerified
  ) {
    if (user) {
      return redirectToDestination(pathname || `/${intl.locale}/account`, source);
    }
    return redirectToLogin(source);
  }

  // Hold a loading state briefly till we confirm signup token and decide
  if (user || !customer) {
    return <StyledLoading />;
  }

  const navBar = <NavBar items={navItems} size="lg" />;

  const renderSignUpBlock = () => (
    <Container>
      <StyledSignUpBlock sm="8" lg="7">
        <XpayPaypalAlert />
        <StyledSignUpHeadingBlock
          title={intl.formatMessage(messages.headingBlockTitle)}
          leadText={intl.formatMessage(messages.headingBlockLeadText)}
        />
        <ConfirmSignUpForm
          customer={customer}
          user={user}
          id={id}
          confirmSignUp={confirmSignUp}
          source={source}
          loading={loading}
        />
      </StyledSignUpBlock>
    </Container>
  );

  const renderMobileDOM = () => (
    <>
      <StyledBGWrapper $rtl={intl.bidi} />
      {renderSignUpBlock()}
    </>
  );

  const renderNonMobileDOM = () => <StyledBGWrapper $rtl={intl.bidi}>{renderSignUpBlock()}</StyledBGWrapper>;

  return (
    <StyledLayout navBar={navBar} footer={<Footer />} spacing="lg">
      <PageMeta seoTitle={intl.formatMessage(messages.seoTitle)} />
      {isMobile ? renderMobileDOM() : renderNonMobileDOM()}
    </StyledLayout>
  );
};

const mapStateToProps = (state: any) => {
  const { loading, error, data: customerData, validateSignUpTokenError, updateEmailWithTokensError } = state.customer;
  return {
    navItems: state.navs.data.public,
    error,
    validateSignUpTokenError,
    updateEmailWithTokensError,
    user: state.user.data,
    customerData,
    loading,
  };
};

const mapDispatchToProps = {
  validateSignUpToken: actions.validateSignUpToken,
  confirmSignUp: actions.confirmSignUp,
  updateEmailWithTokens: actions.updateEmailWithTokens,
  getCustomerEmail: actions.getCustomerEmail,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfirmSignUp);
